import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { SectionLoader, EmptyState } from "@/components/common";
import { formatDate } from "@/lib/format";
import { Mic, Upload, FileText, Plus, Loader2, Square, Trash2, ArrowRight, Sparkles, AudioLines, Disc3, Video, RotateCcw, DownloadCloud } from "lucide-react";

function StatusBadge({ r }) {
  const map = {
    recording: ["Recording…", "secondary"],
    importing: ["Importing…", "secondary"],
    imported: ["Imported", "secondary"],
    import_failed: ["Import failed", "destructive"],
    draft: ["Draft", "secondary"],
    processing: ["Finalizing…", "secondary"],
    ready: ["Ready", "secondary"],
    transcribing: ["Transcribing…", "secondary"],
    transcribed: ["Transcribed", "secondary"],
    transcribe_failed: ["Transcription failed", "destructive"],
    analyzing: ["Analyzing…", "secondary"],
    analyzed: ["Analyzed", "secondary"],
    analyze_failed: ["Analysis failed", "destructive"],
  };
  const [label, variant] = map[r.status] || [r.status, "secondary"];
  return <Badge variant={variant}>{label}</Badge>;
}

function fmtTime(s) {
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const ss = (s % 60).toString().padStart(2, "0");
  return `${m}:${ss}`;
}

function NewRecordingDialog({ open, onOpenChange, onCreated }) {
  const [tab, setTab] = useState("upload");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast.error("Give the recording a title"); return; }
    const fd = new FormData();
    fd.append("title", title.trim());
    if (tab === "upload") {
      if (!file) { toast.error("Choose an audio file"); return; }
      fd.append("file", file);
    } else {
      if (!transcript.trim()) { toast.error("Paste a transcript"); return; }
      fd.append("transcript_text", transcript.trim());
    }
    setSaving(true);
    try {
      const res = await api.post("/recordings", fd);
      toast.success("Recording created");
      onCreated(res.data.recording_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create recording");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setFile(null); setTranscript(""); setTitle(""); } onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-serif text-xl">New recording</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Title</Label><Input className="mt-1.5" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Friday Conversations — Ep 12" data-testid="rec-title-input" /></div>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="w-full">
              <TabsTrigger value="upload" className="flex-1"><Upload className="mr-1.5 h-4 w-4" />Upload</TabsTrigger>
              <TabsTrigger value="paste" className="flex-1"><FileText className="mr-1.5 h-4 w-4" />Paste</TabsTrigger>
            </TabsList>
            <TabsContent value="upload" className="mt-4">
              <input id="recfile" type="file" accept="audio/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="rec-file-input" />
              <Button asChild variant="secondary" className="w-full"><label htmlFor="recfile" className="cursor-pointer"><Upload className="mr-2 h-4 w-4" />{file ? file.name : "Choose an audio file"}</label></Button>
              <p className="mt-2 text-xs text-muted-foreground">MP3, WAV, M4A, WEBM up to 60MB.</p>
            </TabsContent>
            <TabsContent value="paste" className="mt-4">
              <Textarea rows={6} value={transcript} onChange={(e) => setTranscript(e.target.value)} placeholder={"Paste a transcript. Use 'Name: text' per line for speakers, e.g.\nMaria: Trust is about follow-up.\nDavid: Referrals matter most."} data-testid="rec-transcript-input" />
              <p className="mt-2 text-xs text-muted-foreground">Great for analyzing an existing transcript right away (no audio needed).</p>
            </TabsContent>
          </Tabs>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="rec-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminRecordings() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [retrying, setRetrying] = useState("");
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/recordings").then((x) => x.data).catch(() => []);
    setItems(r);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const importBroadcasts = async () => {
    setImporting(true);
    try {
      const r = await api.post("/recordings/import/cloudflare");
      const d = r.data || {};
      if (d.configured === false) {
        toast.error("Live broadcast recording isn't configured yet.");
      } else if (d.imported > 0) {
        toast.success(`Imported ${d.imported} broadcast recording${d.imported === 1 ? "" : "s"}.`);
        load();
      } else {
        toast.info("No new broadcast recordings yet. Cloudflare can take a few minutes to finish processing after you end a broadcast — try again shortly.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not import broadcast recordings");
    } finally { setImporting(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/recordings/${id}`); toast.success("Recording deleted"); load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  const retry = async (id) => {
    setRetrying(id);
    try {
      await api.post(`/recordings/${id}/retry`);
      toast.success("Re-processing started — transcribing again from the saved audio.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not restart processing");
    } finally { setRetrying(""); }
  };

  if (loading) return <SectionLoader />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">Recordings library</h1>
          <p className="mt-1 text-muted-foreground">Every show recorded in the Studio lands here automatically. Upload audio or paste a transcript for AI insights.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild className="bg-red-600 text-white hover:bg-red-700" data-testid="rec-record-session-button">
            <Link to="/studio"><Disc3 className="mr-2 h-4 w-4" />Record a show</Link>
          </Button>
          <Button onClick={importBroadcasts} variant="secondary" disabled={importing} data-testid="rec-import-broadcasts-button">
            {importing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <DownloadCloud className="mr-2 h-4 w-4" />}Import from broadcasts
          </Button>
          <Button onClick={() => setOpen(true)} variant="secondary" data-testid="rec-new-button"><Plus className="mr-2 h-4 w-4" />Upload / paste</Button>
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={AudioLines} title="No recordings yet" description="Record a show in the Studio, upload an audio file, or paste a transcript to get AI-powered insights." action={<Button asChild className="bg-red-600 text-white hover:bg-red-700"><Link to="/studio"><Disc3 className="mr-2 h-4 w-4" />Open the Studio</Link></Button>} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((r) => (
            <Card key={r.recording_id} className="border-border bg-card transition-shadow hover:shadow-[0_12px_30px_rgba(20,20,20,0.08)]" data-testid="rec-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <StatusBadge r={r} />
                  <div className="flex items-center gap-1.5">
                    {r.has_video && <Badge variant="secondary"><Video className="mr-1 h-3 w-3" />Video</Badge>}
                    {r.is_sandbox && <Badge className="border border-amber-200 bg-amber-50 text-amber-800">Sandbox</Badge>}
                    {r.has_analysis && <Badge className="border border-primary/30 bg-primary/10 text-primary"><Sparkles className="mr-1 h-3 w-3" />AI</Badge>}
                  </div>
                </div>
                <h3 className="mt-3 line-clamp-2 font-serif text-lg font-semibold text-foreground">{r.title}</h3>
                <p className="mt-1 text-xs text-muted-foreground">{r.source} · {formatDate(r.created_at)}</p>
                {(r.status === "transcribe_failed" || r.status === "analyze_failed") && (r.transcribe_error || r.analyze_error) && (
                  <p className="mt-2 line-clamp-2 text-xs text-destructive" data-testid="rec-error-text">{r.transcribe_error || r.analyze_error}</p>
                )}
                <div className="mt-4 flex items-center justify-between gap-2">
                  <Button asChild size="sm" variant="secondary"><Link to={`/admin/recordings/${r.recording_id}`}>Open <ArrowRight className="ml-1 h-3.5 w-3.5" /></Link></Button>
                  <div className="flex items-center gap-1.5">
                    {["transcribe_failed", "analyze_failed", "transcribing"].includes(r.status) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => retry(r.recording_id)}
                        disabled={retrying === r.recording_id}
                        data-testid="rec-retry-button"
                      >
                        {retrying === r.recording_id
                          ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                          : <RotateCcw className="mr-1.5 h-3.5 w-3.5" />}
                        Reset &amp; retry
                      </Button>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button size="icon" variant="ghost" className="text-destructive" data-testid="rec-delete-button"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete recording?</AlertDialogTitle><AlertDialogDescription>This permanently removes the audio, transcript and analysis.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(r.recording_id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <NewRecordingDialog open={open} onOpenChange={setOpen} onCreated={(id) => { setOpen(false); navigate(`/admin/recordings/${id}`); }} />
    </div>
  );
}
